import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SHOP_URL, bagsOf, useShop, useFlavourStock } from "./shop";
import { FLAVOURS } from "@/data/content";
import { trackAddToCart } from "@/components/site/ShopifyAnalytics";
import { track } from "./track";

const CartCtx = createContext(null);
const KEY = "swolebears-cart";

const load = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(v) ? v.map((i) => ({ ...i, key: i.key || String(i.variant_id) })) : [];
  } catch { return []; }
};

export const mixText = (mix) => (Array.isArray(mix) ? mix.map((m) => `${m.qty} ${m.name}`).join(", ") : "");
// Bags each cart line uses per flavour name: box mixes, plus single-flavour packs (e.g. a 6 pack = 6 bags)
export const lineBags = (i) => {
  if (Array.isArray(i.mix)) return i.mix.map((m) => ({ name: m.name, qty: m.qty }));
  const f = FLAVOURS.find((x) => (i.title || "").toLowerCase().includes(x.match));
  return f ? [{ name: f.name, qty: bagsOf(i.title) }] : [];
};
// Bags per flavour name already in the cart
export const boxBags = (items) => {
  const out = {};
  items.forEach((i) => lineBags(i).forEach((m) => { out[m.name] = (out[m.name] || 0) + m.qty * i.qty; }));
  return out;
};

const b64 = (obj) => encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(obj)))));

export const GIFT_KEY = "swolebears-gift";
export const loadGift = () => {
  try { return JSON.parse(localStorage.getItem(GIFT_KEY)) || null; } catch { return null; }
};
export const saveGift = (g) => { localStorage.setItem(GIFT_KEY, JSON.stringify(g)); window.dispatchEvent(new Event("sb-gift")); };

const buildCheckoutUrl = (items, gift) => {
  if (!items.length) return null;
  const boxes = items.filter((i) => i.mix);
  const ordered = boxes.length ? [boxes[0], ...items.filter((i) => i !== boxes[0])] : items;
  const qty = new Map();
  ordered.forEach((i) => qty.set(i.variant_id, (qty.get(i.variant_id) || 0) + i.qty));
  const url = `${SHOP_URL}/cart/${[...qty].map(([id, q]) => `${id}:${q}`).join(",")}`;
  const params = [];
  const notes = [];
  if (boxes.length) {
    params.push(`properties=${b64({ "Box mix": mixText(boxes[0].mix) })}`);
    notes.push(`Build-your-box mix: ${boxes.map((b) => `${b.title} x${b.qty}: ${mixText(b.mix)}`).join(" | ")}`);
  }
  if (gift?.note) notes.push(gift.note);
  if (notes.length) params.push(`note=${encodeURIComponent(notes.join(" || "))}`);
  if (gift?.code) params.push(`discount=${encodeURIComponent(gift.code)}`);
  return params.length ? `${url}?${params.join("&")}` : url;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);
  const [gift, setGift] = useState(loadGift);

  useEffect(() => {
    const sync = () => setGift(loadGift());
    window.addEventListener("sb-gift", sync);
    return () => window.removeEventListener("sb-gift", sync);
  }, []);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const addLine = useCallback((line) => {
    setItems((cur) => {
      const found = cur.find((i) => i.key === line.key);
      if (found) return cur.map((i) => (i.key === line.key ? { ...i, qty: i.qty + 1 } : i));
      return [...cur, { ...line, qty: 1 }];
    });
  }, []);

  const add = useCallback((p, image) => {
    addLine({ key: String(p.variant_id), variant_id: p.variant_id, title: p.title, price: p.price, image: image || p.image });
    trackAddToCart(p);
    track("add_to_cart");
  }, [addLine]);

  const addBox = useCallback((p, mix, image) => {
    addLine({ key: `${p.variant_id}|${mixText(mix)}`, variant_id: p.variant_id, title: p.title, price: p.price, image: image || p.image, mix });
    trackAddToCart(p);
    track("add_to_cart");
  }, [addLine]);

  const setQty = useCallback((key, qty) => {
    setItems((cur) => (qty <= 0 ? cur.filter((i) => i.key !== key) : cur.map((i) => (i.key === key ? { ...i, qty } : i))));
  }, []);

  const { products, status } = useShop();
  const stock = useFlavourStock();
  const value = useMemo(() => {
    // saved carts can be stale: use live prices, and flag lines that are gone or exceed today's bag counts
    const used = boxBags(items);
    const over = new Set(FLAVOURS.filter((f) => (used[f.name] || 0) > stock[f.key]).map((f) => f.name));
    const live = items.map((i) => {
      if (status !== "ready") return i;
      const p = products.find((x) => String(x.variant_id) === String(i.variant_id));
      const issue = !p || !p.available ? "gone" : lineBags(i).some((m) => over.has(m.name)) ? "stock" : null;
      return p ? { ...i, title: p.title, price: p.price, issue } : { ...i, issue };
    });
    const count = live.reduce((s, i) => s + i.qty, 0);
    const subtotal = live.reduce((s, i) => s + i.qty * i.price, 0);
    const blocked = live.some((i) => i.issue);
    return { items: live, add, addBox, setQty, count, subtotal, blocked, checkoutUrl: blocked ? null : buildCheckoutUrl(live, gift), gift, open, setOpen };
  }, [items, add, addBox, setQty, open, gift, products, status, stock]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => useContext(CartCtx);
